import React, { useState } from 'react';
import { FiPlus, FiEdit2 } from 'react-icons/fi';
import Switch from 'components/shared/Switch';
import AddNotificationModal from './AddNotificationModal';

interface NotificationItem {
  name: string;
  description: string;
  notify: string;
  templateCategory: string;
  userRole: string;
  isEnabled: boolean;
}

const Notifications: React.FC = () => {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  const [notifications, setNotifications] = useState<NotificationItem[]>([
    {
      name: 'New User Signup',
      description: 'New User Signup',
      notify: 'Email',
      templateCategory: 'New Registration',
      userRole: 'Super admin',
      isEnabled: true
    },
    {
      name: 'System Alert',
      description: 'System Alert',
      notify: 'In-app',
      templateCategory: 'System alerts',
      userRole: 'Super admin',
      isEnabled: true
    },
    {
      name: 'Subscription Renewal',
      description: 'Subscription Renewal',
      notify: 'In-app',
      templateCategory: 'Subscriptions and plans',
      userRole: 'Billing admin',
      isEnabled: true
    }
  ]);

  const handleToggleNotification = (index: number) => {
    const updated = [...notifications];
    updated[index].isEnabled = !updated[index].isEnabled;
    setNotifications(updated);
  };

  const handleAddOrEditNotification = (data: Omit<NotificationItem, 'isEnabled'>) => {
    if (editIndex !== null) {
      const updated = [...notifications];
      updated[editIndex] = { ...updated[editIndex], ...data };
      setNotifications(updated);
    } else {
      setNotifications([...notifications, { ...data, isEnabled: true }]);
    }
    setEditIndex(null);
  };

  const openAddModal = () => {
    setEditIndex(null);
    setIsAddModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditIndex(index);
    setIsAddModalOpen(true);
  };

  // Pagination
  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNotifications = notifications.slice(startIndex, endIndex);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold text-white mb-5">Notifications and configurations</h2>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden ">
        <table className="w-full">
          <thead>
            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
              <th className="text-left p-4 font-medium">Global notifications</th>
              <th className="text-right p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
              <td className="p-4 text-gray-300">Email notifications</td>
              <td className="p-4 text-gray-300 text-right">
                <Switch checked={emailNotifications} onChange={() => setEmailNotifications(!emailNotifications)} />
              </td>
            </tr>
            <tr className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
              <td className="p-4 text-gray-300">In-app notifications</td>
              <td className="p-4 text-gray-300 text-right">
                <Switch checked={inAppNotifications} onChange={() => setInAppNotifications(!inAppNotifications)} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-10">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-semibold text-white">Notification list</h3>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
          >
            <FiPlus size={16} />
            Add Notification
          </button>
        </div>

        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                <th className="text-left p-4 font-medium">Notification name</th>
                <th className="text-left p-4 font-medium">Description</th>
                <th className="text-left p-4 font-medium">Notify</th>
                <th className="text-left p-4 font-medium">Template category</th>
                <th className="text-left p-4 font-medium">User role</th>
                <th className="text-left p-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentNotifications.map((n, idx) => (
                <tr key={idx} className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]">
                  <td className="p-4 text-gray-300">{n.name}</td>
                  <td className="p-4 text-gray-300">{n.description}</td>
                  <td className="p-4 text-gray-300">{n.notify}</td>
                  <td className="p-4 text-gray-300">{n.templateCategory}</td>
                  <td className="p-4 text-gray-300">{n.userRole}</td>
                  <td className="p-4 flex gap-3 items-center">
                    <Switch
                      checked={n.isEnabled}
                      onChange={() => handleToggleNotification(startIndex + idx)}
                    />
                    <button onClick={() => openEditModal(startIndex + idx)} className="text-gray-400 hover:text-white">
                      <FiEdit2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
          <div>
            Showing {startIndex + 1}-{Math.min(endIndex, notifications.length)} from {notifications.length}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="p-2 hover:text-white disabled:opacity-50">←</button>
            {pages.map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded flex items-center justify-center ${currentPage === page ? 'bg-[#3A3A3A] text-white' : 'hover:text-white'
                  }`}
              >
                {page}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 hover:text-white disabled:opacity-50">→</button>
          </div>
        </div>
      </div>

      {isAddModalOpen && (
        <AddNotificationModal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
          setEditIndex(null);
        }}
        onSave={handleAddOrEditNotification}
        initialData={editIndex !== null ? notifications[editIndex] : null}
        isEdit={editIndex !== null}
        />
      )}
    </div>
  );
};

export default Notifications;
